/**
 * Theme handling.
 *
 * Three states, not two: `system` is the default, because a visitor who has set
 * a preference at the operating-system level has already answered the question.
 * `light` and `dark` are explicit overrides.
 */

export const THEMES = ['light', 'dark', 'system'] as const;
export type ThemePreference = (typeof THEMES)[number];
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'hs-theme';
export const THEME_ATTRIBUTE = 'data-theme';

export function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value);
}

export function resolveTheme(preference: ThemePreference, prefersDark: boolean): ResolvedTheme {
  if (preference === 'system') return prefersDark ? 'dark' : 'light';
  return preference;
}

/**
 * Applied to <html> before first paint by the inline script below.
 *
 * The attribute is stamped rather than a class toggled because tokens.css keys
 * dark mode off `:root[data-theme='dark']`, and because an attribute is
 * unambiguous to read back from the server-rendered HTML.
 */
export function applyTheme(theme: ResolvedTheme, root: HTMLElement): void {
  root.setAttribute(THEME_ATTRIBUTE, theme);
  root.style.colorScheme = theme;
}

/**
 * The blocking script that runs before the first paint.
 *
 * This must be inline and synchronous in <head>. Anything else — a deferred
 * script, a React effect, a stylesheet — runs after the browser has already
 * painted the light theme, and a dark-mode visitor gets a white flash on every
 * navigation. It is small enough to be worth the blocking cost.
 *
 * It is served with a CSP nonce; the strict policy has no `unsafe-inline`.
 */
export const themeInitScript = `
(function(){
  try {
    var stored = localStorage.getItem('${THEME_STORAGE_KEY}');
    var preference = (stored === 'light' || stored === 'dark' || stored === 'system')
      ? stored
      : 'system';
    var prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    var theme = preference === 'system' ? (prefersDark ? 'dark' : 'light') : preference;
    document.documentElement.setAttribute('${THEME_ATTRIBUTE}', theme);
    document.documentElement.style.colorScheme = theme;
  } catch (e) {
    // localStorage can throw in a partitioned or private context. The default
    // light theme is a correct outcome, not a failure worth reporting.
  }
})();
`.trim();
